import { Injectable, NgZone, PLATFORM_ID, Inject } from '@angular/core';
import { fromEvent, merge, timer, BehaviorSubject, Subscription, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class IdleService {
  private readonly idleTimeoutMs = 55 * 60 * 1000; // 55 minutes
  private readonly warningTimeoutMs = 5 * 60 * 1000; // 5 minutes
  private readonly activityEvents$: Observable<Event>;
  private idleSub?: Subscription;
  private warningSub?: Subscription;
  countdown$ = new BehaviorSubject<number>(0);
  isWarningActive$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly ngZone: NgZone, 
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      const win = window;
      this.activityEvents$ = merge(
        fromEvent(win, 'mousemove'),
        fromEvent(win, 'keydown'),
        fromEvent(win, 'click'),
        fromEvent(win, 'scroll'),
        fromEvent(win, 'touchstart')
      ).pipe(debounceTime(100)); // Optional: smooth out rapid events
    } else {
      this.activityEvents$ = new Observable();
    }
  }

  startWatching(onTimeout: () => void) {
    this.ngZone.runOutsideAngular(() => {
      this.resetTimers(onTimeout);
      this.activityEvents$.subscribe(() => this.resetTimers(onTimeout));
    });
  }

  private resetTimers(onTimeout: () => void) {
    this.idleSub?.unsubscribe();
    this.warningSub?.unsubscribe();
    this.isWarningActive$.next(false);
    this.countdown$.next(0);

    this.idleSub = timer(this.idleTimeoutMs).subscribe(() => {
      this.startWarningCountdown(onTimeout);
    });
  }

  private startWarningCountdown(onTimeout: () => void) {
    this.ngZone.run(() => this.isWarningActive$.next(true));

    const warningDuration = this.warningTimeoutMs;
    const warningEndTime = Date.now() + warningDuration;

    this.warningSub = timer(0, 1000).subscribe(() => {
      const now = Date.now();
      const remainingMs = warningEndTime - now;
      const remainingSec = Math.max(0, Math.round(remainingMs / 1000));

      this.ngZone.run(() => this.countdown$.next(remainingSec));

      if (remainingSec <= 0) {
        this.warningSub?.unsubscribe();
        this.ngZone.run(() => {
          this.isWarningActive$.next(false);
          onTimeout();
        });
      }
    });
  }

  stopWatching() {
    this.idleSub?.unsubscribe();
    this.warningSub?.unsubscribe();
    this.countdown$.complete();
    this.isWarningActive$.complete();
  }
}
